import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { IdArrayInput } from './useTablePages';

type Anchor = { index: number; selected: boolean };

/** Stops the browser from highlighting text when shift-clicking to select a range. */
export const preventShiftTextSelection = (e: ReactMouseEvent) => {
  if (e.shiftKey) {
    e.preventDefault();
  }
};

/**
 * Checkbox selection with shift-click range support.
 *
 * Returns a handler to call when a row's checkbox is clicked. A plain click
 * toggles that row and becomes the anchor; a shift-click applies the anchor's
 * action (select or deselect) to every visible row between the anchor and the
 * clicked row. The anchor resets whenever the visible row order changes
 * (page/sort/filter).
 */
const useRangeSelection = <IdValue extends string | number>(
  itemIds: IdArrayInput<IdValue>,
  selectedIds: IdArrayInput<IdValue> | undefined,
  onSelectionChange?: (selectedIds: IdValue[]) => void
) => {
  const anchorRef = useRef<Anchor | null>(null);

  const prevItemIdsRef = useRef(itemIds);
  useEffect(() => {
    const prev = prevItemIdsRef.current;
    if (prev !== itemIds) {
      const sameOrder =
        prev.length === itemIds.length &&
        prev.every((id, i) => id === itemIds[i]);
      if (!sameOrder) {
        anchorRef.current = null;
      }
      prevItemIdsRef.current = itemIds;
    }
  }, [itemIds]);

  return useCallback(
    (index: number, shiftKey: boolean) => {
      if (!onSelectionChange) {
        return;
      }
      const currentIds: IdValue[] = selectedIds ? selectedIds.slice() : [];
      const anchor = anchorRef.current;

      if (shiftKey && anchor && anchor.index !== index) {
        const start = Math.min(anchor.index, index);
        const end = Math.max(anchor.index, index);
        const range = itemIds.slice(start, end + 1);
        if (anchor.selected) {
          const existing = new Set<IdValue>(currentIds);
          onSelectionChange(
            currentIds.concat(range.filter((id) => !existing.has(id)))
          );
        } else {
          const rangeIds = new Set<IdValue>(range);
          onSelectionChange(currentIds.filter((id) => !rangeIds.has(id)));
        }
        return;
      }

      const itemId = itemIds[index];
      const selectedIndex = currentIds.indexOf(itemId);
      const selecting = selectedIndex === -1;
      if (selecting) {
        currentIds.push(itemId);
      } else {
        currentIds.splice(selectedIndex, 1);
      }
      anchorRef.current = { index, selected: selecting };
      onSelectionChange(currentIds);
    },
    [itemIds, selectedIds, onSelectionChange]
  );
};

export default useRangeSelection;
